import React, { useCallback, useEffect, useState } from 'react';
import {
  ClosingReason,
  TenureCloseReturn,
  TenureMemberReturn,
  TenureReturn,
} from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../common/Catalyst/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Input } from '../common/Catalyst/input';
import { Subheading } from '../common/Catalyst/heading';
import { Text } from '../common/Catalyst/text';
import { FieldPicker } from '../common/Custom/FieldPicker';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
} from '../utils/billingHelpers';
import {
  closingReason,
  memberRole,
  tenureStatus,
  turnoverBalancePolicy,
} from './enumLabels';

interface Props {
  homeId: string;
  canManage: boolean;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The 16 digits, carved.
 *
 * `hoa_code(7) + home_seq(5) + tenure(3) + luhn(1)` — annotating it is
 * how somebody reading a lockbox file works out which part changed when
 * a home turned over.
 */
const AccountNumber: React.FC<{ value: string }> = ({ value }) => {
  if (value.length !== 16) {
    return <span className="font-mono">{value}</span>;
  }
  const parts = [
    { text: value.slice(0, 7), label: 'association' },
    { text: value.slice(7, 12), label: 'home' },
    { text: value.slice(12, 15), label: 'owner' },
    { text: value.slice(15), label: 'check' },
  ];
  return (
    <span className="inline-flex flex-wrap items-end gap-2">
      {parts.map((p) => (
        <span key={p.label} className="inline-flex flex-col items-center">
          <span className="font-mono">{p.text}</span>
          <span className="text-[10px] uppercase tracking-wide text-zinc-400">
            {p.label}
          </span>
        </span>
      ))}
    </span>
  );
};

/**
 * Ownership of a home, over time.
 *
 * **HOLD is tri-state and the third state is the default.** Forced on,
 * forced off, or following the collections policy — and `null` is not
 * the same as `false`. A checkbox cannot say that, so this is a
 * three-way choice and the current value is always visible.
 */
const TenureList: React.FC<Props> = ({ homeId, canManage }) => {
  const [tenures, setTenures] = useState<TenureReturn[]>([]);
  const [members, setMembers] = useState<Record<string, TenureMemberReturn[]>>(
    {}
  );
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState<{
    type: 'success' | 'error';
    title: string;
    message: string;
  } | null>(null);

  const [closing, setClosing] = useState(false);
  const [reason, setReason] = useState<ClosingReason>('sale');
  const [closingDate, setClosingDate] = useState('');
  // The server's refusal shows above the form; a malformed date is the
  // field's own problem and shows under it.
  const [closeError, setCloseError] = useState('');
  const [dateError, setDateError] = useState('');
  const [outcome, setOutcome] = useState<TenureCloseReturn | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await billingService.listTenures({ homeId });
      setTenures(data.tenures);
      const lists = await Promise.all(
        data.tenures.map((t) =>
          billingService
            .listTenureMembers({ tenureId: t.id })
            .then((r) => [t.id, r.data.members] as const)
        )
      );
      setMembers(Object.fromEntries(lists));
    } catch (err) {
      console.error('Error loading tenures:', err);
      setAlert({
        type: 'error',
        title: "Couldn't load ownership",
        message: billingErrorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  }, [homeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const setHold = async (
    tenure: TenureReturn,
    hold: boolean | null
  ): Promise<void> => {
    try {
      setBusy(true);
      await billingService.setTenureHold({
        tenureId: tenure.id,
        // `null` is a real value here — it means "follow the policy" —
        // so it has to be sent rather than omitted.
        holdRequest: { hold },
      });
      await load();
    } catch (err) {
      console.error('Error setting hold:', err);
      setAlert({
        type: 'error',
        title: "Couldn't change HOLD",
        message: billingErrorMessage(err),
      });
    } finally {
      setBusy(false);
    }
  };

  const closeTenure = async (): Promise<void> => {
    if (closingDate && !DATE.test(closingDate)) {
      setDateError('Use a date, or leave blank for today.');
      return;
    }
    try {
      setBusy(true);
      setDateError('');
      setCloseError('');
      const { data } = await billingService.closeTenure({
        homeId,
        closeTenureRequest: {
          closing_reason: reason,
          closing_date: closingDate || null,
        },
      });
      setOutcome(data);
      setClosing(false);
      await load();
    } catch (err) {
      console.error('Error closing tenure:', err);
      setCloseError(billingErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading && tenures.length === 0) return <LoadingSpinner />;

  const active = tenures.find((t) => t.status === 'active');

  return (
    <div>
      {alert && (
        <div className="mb-4">
          <ShowAlert
            alertType={alert.type}
            alertTitle={alert.title}
            alertMessage={alert.message}
            isVisible={!!alert}
            onClose={() => setAlert(null)}
            timeout={12000}
            isInline
          />
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Subheading>Ownership</Subheading>
          <Text className="mt-1">
            Who owns this home, the account number the bank keys on, and who may
            pay against it. A sale retires the account number and mints a new
            one rather than editing the old.
          </Text>
        </div>
        {canManage && active && (
          <Button color="sky" onClick={() => setClosing(true)}>
            Record a turnover
          </Button>
        )}
      </div>

      {outcome && (
        <div className="mt-4 rounded-lg border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/40 p-4">
          <Subheading>Turnover recorded</Subheading>
          <Text className="mt-1">
            {formatMoney(outcome.outstanding_at_closing)} was outstanding on{' '}
            {formatBillingDate(outcome.closing_date)}.{' '}
            {outcome.carried_forward ? (
              <>
                Under {turnoverBalancePolicy.label(outcome.balance_policy)} it
                has moved to the new owner&rsquo;s account, so it is in their
                Amount Due.
              </>
            ) : (
              <>
                Under {turnoverBalancePolicy.label(outcome.balance_policy)} it
                stays on the retired account — out of the new owner&rsquo;s
                Amount Due, still collectible from the previous owner or through
                the lien.
              </>
            )}
          </Text>
          <Text className="mt-2 text-sm">
            New account number: {outcome.opened.account_number} · retired:{' '}
            {outcome.retired.account_number}
          </Text>
        </div>
      )}

      <div className="mt-6 space-y-4">
        {tenures.map((t) => {
          const own = members[t.id] ?? [];
          return (
            <div
              key={t.id}
              className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge color={tenureStatus.color(t.status)}>
                      {tenureStatus.label(t.status)}
                    </Badge>
                    <Text className="text-sm">
                      Owner {t.tenure_seq} ·{' '}
                      {t.started_on
                        ? `from ${formatBillingDate(t.started_on)}`
                        : 'start not recorded'}
                      {t.ended_on ? ` to ${formatBillingDate(t.ended_on)}` : ''}
                      {t.closing_reason
                        ? ` · ${closingReason.label(t.closing_reason)}`
                        : ''}
                    </Text>
                  </div>
                  <div className="mt-3">
                    <AccountNumber value={t.account_number} />
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <Text className="text-sm font-medium">On the account</Text>
                {own.length === 0 ? (
                  <Text className="mt-1 text-sm">
                    Nobody — this account emits no payer-file rows, so the
                    lockbox will not accept a payment for it.
                  </Text>
                ) : (
                  <ul className="mt-1 space-y-1">
                    {own.map((m) => (
                      <li key={m.id} className="text-sm">
                        {m.member_id} · {memberRole.label(m.role)}
                        {m.is_billing_payer && (
                          <Badge color="sky" className="ml-2">
                            Billing payer
                          </Badge>
                        )}
                        {!m.include_on_payer_file && (
                          <Badge color="zinc" className="ml-2">
                            Not on the payer file
                          </Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {t.status === 'active' && (
                <div className="mt-4">
                  <Text className="text-sm font-medium">Payer file HOLD</Text>
                  <Text className="mt-1 text-xs">
                    Column 20 of the lockbox file. Left to the policy it follows
                    this home&rsquo;s collections case; forcing it either way
                    overrides that — for a disputed fine, a payment plan, a unit
                    in litigation.
                  </Text>
                  <div className="mt-2 inline-flex rounded-md border border-zinc-300 dark:border-zinc-700 overflow-hidden">
                    {[
                      { value: true, label: 'Force HOLD' },
                      { value: false, label: 'Suppress HOLD' },
                      { value: null, label: 'Follow the policy' },
                    ].map((opt) => {
                      const current = (t.hold_override ?? null) === opt.value;
                      return (
                        <button
                          key={String(opt.value)}
                          type="button"
                          disabled={!canManage || busy}
                          onClick={() => setHold(t, opt.value)}
                          className={
                            current
                              ? 'px-3 py-1.5 text-sm bg-sky-600 text-white'
                              : 'px-3 py-1.5 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50'
                          }
                          aria-pressed={current}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={closing} onClose={() => setClosing(false)} size="xl">
        <DialogTitle>Record a turnover</DialogTitle>
        <DialogBody>
          {closeError && (
            <div className="mb-4">
              <ShowAlert
                alertType="error"
                alertTitle="The turnover was not recorded"
                alertMessage={closeError}
                isVisible
                onClose={() => setCloseError('')}
                timeout={0}
                isInline
              />
            </div>
          )}
          <Text className="mb-4">
            This retires the current account number and mints a new one. What
            happens to any unpaid balance is this association&rsquo;s
            <strong>
              {' '}
              {turnoverBalancePolicy.label(
                // Shown from the outcome once known; before that the
                // policy page is the authority.
                'settle_at_closing'
              )}
            </strong>{' '}
            setting or its alternative — the result is reported back once it is
            done.
          </Text>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Reason</Label>
                <FieldPicker
                  label="Reason"
                  value={reason}
                  onChange={(v) => setReason(v as ClosingReason)}
                  options={closingReason.options().map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
                {closingReason.help(reason) && (
                  <Description className="mt-1">
                    {closingReason.help(reason)}
                  </Description>
                )}
              </Field>
              <Field>
                <Label>Closing date</Label>
                <Description>Blank means today.</Description>
                <Input
                  type="date"
                  value={closingDate}
                  onChange={(e) => {
                    setClosingDate(e.target.value);
                    setDateError('');
                  }}
                  invalid={!!dateError}
                  className="max-w-[14rem]"
                />
                {dateError && <ErrorMessage>{dateError}</ErrorMessage>}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setClosing(false)} disabled={busy}>
            Cancel
          </Button>
          <Button color="red" onClick={closeTenure} disabled={busy}>
            {busy ? 'Recording…' : 'Retire and re-mint'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TenureList;
